/**
 * Status badge + helper text for the v32 draft-approval state machine.
 *
 * Vocabulary tweak: brand-side UI shows "Passed" instead of "Rejected" so the
 * creator-facing tone (and the brand's own internal language) is gentler.
 */
import React from 'react';

export const DRAFT_STATE_META = {
  pending: { label: 'Pending', color: 'gray', help: 'RFP sent — awaiting creator response.' },
  accepted: { label: 'Accepted', color: 'cyan', help: 'Creator accepted the RFP.' },
  content_uploaded: { label: 'Draft Submitted', color: 'amber', help: 'Awaiting your review — 48-hour SLA.' },
  revision: { label: 'Revisions Requested', color: 'orange', help: 'Creator is reworking the draft.' },
  approved: { label: 'Approved', color: 'emerald', help: 'Cleared to post live.' },
  posted: { label: 'Posted', color: 'green', help: 'Live on platform.' },
  rejected: { label: 'Passed', color: 'rose', help: 'Brand passed; escrow refunded.' }, // softer wording
  disputed: { label: 'Mediation', color: 'red', help: 'In dispute — support is reviewing.' },
};

const COLOR_CLASSES = {
  gray: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
  cyan: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  orange: 'bg-orange-500/10 text-orange-300 border-orange-500/20',
  emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  green: 'bg-green-500/10 text-green-300 border-green-500/20',
  rose: 'bg-rose-500/10 text-rose-300 border-rose-500/20',
  red: 'bg-red-500/10 text-red-300 border-red-500/20',
};

export default function DraftStateBadge({ status, className = '' }) {
  const meta = DRAFT_STATE_META[status] || { label: status || 'Unknown', color: 'gray' };
  const cls = COLOR_CLASSES[meta.color] || COLOR_CLASSES.gray;
  return (
    <span
      data-testid={`draft-state-badge-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-widest ${cls} ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {meta.label}
    </span>
  );
}
